// SectionAct — a detail card's OWN act (ADR-036 §7): the page's sections read
// the entity's registry by the row's `section` — `<DetailSection action={<SectionAct
// acts={acts} section="members" />}>` — never a hand-wired button, so the card's
// act, the peek's Edit row and the drawer's title are ONE string from ONE row.
// The face is the section-edit idiom (FRONTEND-PATTERNS §Section-body): a link
// button, TEXT ONLY — the verb says it all, a glyph beside a blue link adds
// nothing (the architect, at the team's live look; the act's glyph serves the
// menus and the seat); a route run is a real link; a row with `choices` opens
// the chooser (ADR-038 §1 — mounted here, once per surface); a row outside the
// viewer's reach keeps its face DISABLED with the note under it (the zones'
// "Outside your scope" — said, never hidden). Nothing when the registry has no
// act for the section (a viewer without the key sees no link).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button.js';
import { Link } from '../ui/Link.js';
import { ChoiceDialog } from './ChoiceDialog.js';
import { runAct, sectionActOf, type EntityAct } from './entityRegistry.js';

export interface SectionActProps {
  /** The entity's resolved acts. */
  acts: readonly EntityAct[];
  /** The `DetailSection` id the act belongs to (the row's `section`). */
  section: string;
}

export function SectionAct({ acts, section }: SectionActProps) {
  const navigate = useNavigate();
  const [chooserOpen, setChooserOpen] = useState(false);
  const act = sectionActOf(acts, section);
  if (!act) return null;

  if (act.disabled) {
    return (
      <span className="flex flex-col items-end gap-0.5">
        <Button variant="link" disabled>
          {act.label}
        </Button>
        {act.disabledNote && <span className="text-xs text-[var(--color-text-secondary)]">{act.disabledNote}</span>}
      </span>
    );
  }

  if (act.run && 'route' in act.run) {
    return (
      <Link to={act.run.route} className="text-sm font-medium">
        {act.label}
      </Link>
    );
  }

  return (
    <>
      <Button variant="link" onClick={() => (act.choices ? setChooserOpen(true) : act.run && runAct(act.run, navigate))}>
        {act.label}
      </Button>
      {act.choices && act.question && (
        <ChoiceDialog
          open={chooserOpen}
          onClose={() => setChooserOpen(false)}
          title={act.question}
          choices={act.choices}
          onChoose={(id) => {
            const choice = act.choices?.find((c) => c.id === id);
            if (choice) runAct(choice.run, navigate);
          }}
        />
      )}
    </>
  );
}
